import {
  TABLE_DIVIDER_WIDTH,
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_PAGINATION_HEIGHT,
  TABLE_ROW_HEIGHT_CSS_VALUE,
} from '@/features/shared/components/tableSystem/tableConstants';
import { resolveTableDividerColor } from '@/features/shared/components/tableSystem/tableDividerSx';
import { s, typographyTokens } from '@/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';

export const tableSx: SxProps<Theme> = {
  '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnSeparator, & .MuiDataGrid-columnHeaders .MuiDataGrid-iconSeparator':
    {
      display: 'none !important',
      opacity: '0 !important',
      visibility: 'hidden !important',
      pointerEvents: 'none !important',
    },
  '& .MuiDataGrid-columnsManagementHeader': {
    display: 'none !important',
  },
  '& .MuiDataGrid-columnsManagementFooter': {
    display: 'none !important',
  },
  '& .MuiDataGrid-paper': {
    backgroundColor: 'background.paper',
    borderRadius: 1,
    boxShadow: 'none',
    overflow: 'hidden',
  },
  '& .MuiDataGrid-panelWrapper': {
    p: 0,
    backgroundColor: 'background.paper',
  },
  '& .MuiDataGrid-panelContent': {
    p: 0,
    backgroundColor: 'background.paper',
  },
  '& .MuiDataGrid-columnsManagement': {
    p: 0,
    backgroundColor: 'background.paper',
  },
  '& .MuiDataGrid-columnsManagementRow': {
    margin: 0,
    px: s('sm'),
    py: s('xs'),
    minHeight: 44,
    borderRadius: 0,
  },
  '& .MuiDataGrid-columnsManagementRow .MuiFormControlLabel-label': {
    fontSize: '15px',
  },
  '& .MuiDataGrid-menuList, & .MuiDataGrid-menuList .MuiMenuItem-root': {
    backgroundColor: 'background.paper',
  },
  '& .MuiDataGrid-menuList .MuiMenuItem-root': {
    minHeight: 40,
    px: s('sm'),
    py: s('xs'),
  },
  '& .MuiDataGrid-menuList .MuiMenuItem-root.Mui-selected': {
    backgroundColor: 'action.hover',
  },
  '& .MuiDataGrid-menuList .MuiMenuItem-root.Mui-selected:hover': {
    backgroundColor: 'action.selected',
  },
  '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent': {
    borderLeft: 'none !important',
    borderRight: 'none !important',
  },
  '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--withRightBorder, & .MuiDataGrid-columnHeader--withLeftBorder':
    {
      borderRight: 'none !important',
      borderLeft: 'none !important',
    },
  '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
    borderRight: 'none !important',
    borderLeft: 'none !important',
  },
  '& .MuiDataGrid-columnHeaders': {
    minHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
    maxHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
    height: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
    alignItems: 'center',
  },
  '& .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner, & .MuiDataGrid-virtualScrollerContent, & .MuiDataGrid-filler':
    {
      backgroundColor: 'background.paper',
    },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '15px',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  '& .MuiDataGrid-columnHeader': {
    minHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
    maxHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
    height: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
    alignItems: 'center',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: s('sm'),
      bottom: s('sm'),
      right: 0,
      width: `${String(TABLE_DIVIDER_WIDTH)}px`,
      backgroundColor: (muiTheme: Theme) => resolveTableDividerColor(muiTheme),
      pointerEvents: 'none',
    },
  },
  '& .MuiDataGrid-columnHeader:last-of-type::after': {
    display: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeaderTitleContainerContent':
    {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      paddingLeft: s('xs'),
      paddingRight: s('xs'),
    },
  '& .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-cell--withLeftBorder': {
    borderRight: 'none !important',
    borderLeft: 'none !important',
  },
  '& .MuiDataGrid-row': {
    minHeight: `${TABLE_ROW_HEIGHT_CSS_VALUE} !important`,
    maxHeight: `${TABLE_ROW_HEIGHT_CSS_VALUE} !important`,
    height: `${TABLE_ROW_HEIGHT_CSS_VALUE} !important`,
    width: '100% !important',
    maxWidth: '100% !important',
    margin: '0 !important',
    borderRadius: '0 !important',
    boxSizing: 'border-box !important',
    boxShadow: 'none !important',
  },
  '& .MuiDataGrid-cell': {
    minHeight: `${TABLE_ROW_HEIGHT_CSS_VALUE} !important`,
    maxHeight: `${TABLE_ROW_HEIGHT_CSS_VALUE} !important`,
    height: `${TABLE_ROW_HEIGHT_CSS_VALUE} !important`,
    py: s('none'),
    fontSize: '15px',
    fontWeight: typographyTokens.body.fontWeight,
    alignItems: 'flex-start',
    boxSizing: 'border-box !important',
    boxShadow: 'none !important',
  },
  '&& .MuiDataGrid-footerContainer.MuiDataGrid-withBorderColor, && .MuiDataGrid-footerContainer': {
    borderTop: (muiTheme) =>
      `${String(TABLE_DIVIDER_WIDTH)}px solid ${resolveTableDividerColor(muiTheme)} !important`,
    boxShadow: 'none !important',
    minHeight: `${TABLE_PAGINATION_HEIGHT}px !important`,
    maxHeight: `${TABLE_PAGINATION_HEIGHT}px !important`,
    height: `${TABLE_PAGINATION_HEIGHT}px !important`,
    flex: `0 0 ${TABLE_PAGINATION_HEIGHT}px !important`,
    boxSizing: 'border-box !important',
  },
  '& .MuiDataGrid-cellContent': {
    fontSize: '15px',
    fontWeight: typographyTokens.body.fontWeight,
    alignItems: 'flex-start',
  },
  '& .MuiDataGrid-cell[data-field="name"]': {
    alignItems: 'flex-start',
  },
  '& .MuiDataGrid-cell[data-field="latestRunStatus"]': {
    alignItems: 'flex-start',
  },
  '& .MuiDataGrid-cell[data-field="actions"]': {
    alignItems: 'flex-start',
  },
  '& .MuiDataGrid-cell[data-field="testLatestRunPassed"]': {
    alignItems: 'flex-start',
  },
  '&.MuiDataGrid-loading .suite-latest-pie': {
    visibility: 'hidden',
  },
  '&& .MuiDataGrid-row': {
    backgroundColor: 'background.paper !important',
  },
  '&& .MuiDataGrid-row .MuiDataGrid-cell': {
    backgroundColor: 'inherit !important',
  },
  '&& .MuiDataGrid-row:hover, && .MuiDataGrid-row.Mui-hovered': {
    backgroundColor: 'background.paper !important',
    borderRadius: '0 !important',
  },
  '&& .MuiDataGrid-row:hover .MuiDataGrid-cell, && .MuiDataGrid-row.Mui-hovered .MuiDataGrid-cell':
    {
      backgroundColor: 'inherit !important',
    },
  '&& .MuiDataGrid-row.Mui-selected, && .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: 'background.paper !important',
    borderRadius: '0 !important',
  },
  '&& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell, && .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell':
    {
      backgroundColor: 'inherit !important',
    },
};
